import React from 'react';
import { AlertTriangle, CreditCard, History } from 'lucide-react';
import { Badge } from '../ui/badge';
import { formatoMonto } from '../compartidos/useAnalisis';

const Cvu = ({ v }) => <span className="font-mono text-xs text-slate-800">{v}</span>;

const Sponsor = ({ c }) => (
  <Badge variant="outline" className={c.sponsor === 'Desconocido' ? 'border-amber-300 text-amber-700' : c.sponsor_activo ? 'border-emerald-300 text-emerald-700' : 'text-slate-500'}>
    {c.sponsor}{c.sponsor_activo === false ? ' (anterior)' : ''}
  </Badge>
);

// B2: persona física — CVU activo y CVU anteriores.
const PersonaFisica = ({ cuentas, aviso }) => {
  const activos = cuentas.filter((c) => c.activo);
  const anteriores = cuentas.filter((c) => !c.activo);
  return (
    <div className="space-y-4">
      {aviso && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800" data-testid="aviso-pf-multiples">
          <AlertTriangle size={16} /> {aviso}
        </div>
      )}
      <div data-testid="cvu-activo-bloque">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">CVU activo</p>
        {activos.length === 0 ? <p className="text-sm text-slate-400">Sin CVU activo</p> : activos.map((c) => (
          <div key={c.cvu} className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2">
            <Cvu v={c.cvu} /> <Sponsor c={c} />
            <span className="text-xs text-slate-500">desde {c.desde || '—'}</span>
            {c.reemplaza_a && <span className="text-xs text-indigo-600">reemplaza a …{c.reemplaza_a.slice(-6)}</span>}
          </div>
        ))}
      </div>
      <div data-testid="cvu-anteriores-bloque">
        <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500"><History size={12} /> CVU anteriores ({anteriores.length})</p>
        {anteriores.length === 0 ? <p className="text-sm text-slate-400">Sin CVU anteriores</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-slate-400">
              <th className="py-1">CVU</th><th>Sponsor</th><th>Desde</th><th>Hasta</th><th>Reemplaza a</th><th>Motivo</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {anteriores.map((c) => (
                <tr key={c.cvu}>
                  <td className="py-1.5"><Cvu v={c.cvu} /></td><td><Sponsor c={c} /></td>
                  <td className="text-xs text-slate-500">{c.desde || '—'}</td><td className="text-xs text-slate-500">{c.fecha_estado || '—'}</td>
                  <td className="font-mono text-[11px] text-slate-500">{c.reemplaza_a || '—'}</td>
                  <td className="text-xs text-slate-500">{c.motivo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// B3: persona jurídica — cuentas agrupadas por sucursal o alias, con volumen de 30 días.
const PersonaJuridica = ({ cuentas }) => {
  const grupos = {};
  cuentas.forEach((c) => { (grupos[c.grupo] = grupos[c.grupo] || []).push(c); });
  return (
    <table className="w-full text-sm" data-testid="cuentas-juridica-tabla">
      <thead><tr className="text-left text-[11px] uppercase text-slate-400">
        <th className="py-1">CVU</th><th>Sponsor</th><th>Estado</th><th>Fecha de alta</th><th className="text-right">Volumen 30 días</th>
      </tr></thead>
      {Object.entries(grupos).map(([g, lista]) => (
        <tbody key={g} className="divide-y divide-slate-100">
          <tr><td colSpan={5} className="bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">{g} · {lista.length} cuenta(s)</td></tr>
          {lista.map((c) => (
            <tr key={c.cvu}>
              <td className="py-1.5 pl-2"><Cvu v={c.cvu} /></td><td><Sponsor c={c} /></td>
              <td><Badge className={c.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{c.activo ? 'Activo' : 'Inactivo'}</Badge></td>
              <td className="text-xs text-slate-500">{c.desde || '—'}</td>
              <td className="text-right font-mono text-xs">{c.volumen_30d != null ? formatoMonto(c.volumen_30d) : '—'}</td>
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  );
};

export const ClienteCuentas = ({ ficha }) => {
  if (!ficha) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="cliente-cuentas">
      <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900">
        <CreditCard size={18} className="text-sky-600" />
        {ficha.juridica ? 'Cuentas por sucursal o alias' : 'CVU del cliente'}
        <span className="text-xs font-normal text-slate-400">{ficha.cvus_activos} activo(s) de {ficha.cuentas.length}</span>
      </h2>
      {ficha.juridica ? <PersonaJuridica cuentas={ficha.cuentas} /> : <PersonaFisica cuentas={ficha.cuentas} aviso={ficha.aviso_pf_multiples} />}
    </section>
  );
};

// B4: aviso de datos incompletos.
export const ClienteDatosIncompletos = ({ faltantes }) => (
  faltantes?.length ? (
    <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="cliente-datos-incompletos">
      <AlertTriangle size={16} className="text-amber-600" />
      <span className="font-semibold">Datos incompletos:</span>
      {faltantes.map((f) => <span key={f} className="rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-amber-200">{f}</span>)}
    </div>
  ) : null
);

export default ClienteCuentas;
